import { z } from "zod";

/**
 * Owner-tunable "community" configuration for a PUBLIC workspace, stored as
 * a single `workspaces.community_settings` JSONB blob. Keeping every knob in
 * one client-queried column (instead of a column-per-knob sprawl) keeps the
 * Zero schema small and additive — see the public-workspace community plan.
 *
 * Phase 0 fields only. Later phases extend this object (entry_mode,
 * activity_preview, enabled_signal_types, badge_rules, discoverable, …) by
 * adding optional fields here — additive, no migration. (New fields are
 * written by whichever client knows them; a brief rolling-deploy window may
 * strip an unknown key until the server rolls forward — acceptable for a
 * low-stakes settings blob.)
 */
export const communitySettingsSchema = z.object({
  /** One-line purpose shown above the Join CTA on the public splash. */
  tagline: z.string().max(280).optional(),
  /** Community rules / code of conduct (markdown), shown before joining. */
  rules: z.string().max(8000).optional(),
  /** Owner-curated rooms surfaced as preview cards on the splash. */
  featured_channel_ids: z.array(z.string()).max(12).optional(),
  /** An existing message reused as the pinned "read this first". */
  welcome_message_id: z.string().optional(),
  /**
   * Phase 1 — entry floor. `preview_then_join` lets a non-member read a
   * server-redacted digest of the FEATURED channels before committing;
   * `join_first` keeps the classic join-to-see flow. Default: preview.
   */
  entry_mode: z.enum(["preview_then_join", "join_first"]).optional(),
  /**
   * Phase 5 — anonymous access. When true, a logged-OUT visitor can read the
   * featured-channel digest WITHOUT signing in (lurk-before-signup), with
   * sign-up CTAs throughout. Default (undefined/false) keeps the classic
   * sign-in-to-view behavior. The exposed content is identical to the
   * signed-in preview: server-redacted, FEATURED public channels only — never
   * private channels, DMs, or member emails.
   */
  anonymous_view: z.boolean().optional(),
  /**
   * Phase 5 — search indexing. When true, the public landing + featured
   * content is exposed to search-engine crawlers as server-rendered HTML (via
   * the Vercel edge middleware) and allowed by robots.txt. IMPLIES
   * `anonymous_view` — you cannot index what a logged-out visitor cannot see;
   * the UI enables both together. Default off: never index a community
   * without explicit owner opt-in. Scope is the SAME redacted featured set —
   * indexing never widens what content is exposed, only who may crawl it.
   */
  search_indexing: z.boolean().optional(),
  /**
   * Phase 4 — in-app discovery. When true, the workspace is listed in the
   * cross-workspace community directory. DECOUPLED from `is_public` (a public
   * workspace is not auto-listed) and from `search_indexing` — opting into
   * Google is separate from opting into the in-app browse list. Default off.
   * The directory is server-only (this JSONB predicate doesn't replicate to
   * Zero) and gated by a report/auto-hide moderation mechanism.
   */
  discoverable: z.boolean().optional(),
});

export type CommunitySettings = z.infer<typeof communitySettingsSchema>;

/** Null-safe accessor — treat a missing/empty blob as `{}`. */
export function readCommunitySettings(raw: unknown): CommunitySettings {
  if (raw == null) return {};
  const parsed = communitySettingsSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}
